import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Row, Col, Card, Badge, Pagination } from 'react-bootstrap';
import { FaMapMarkerAlt, FaBriefcase, FaGlobe, FaCloudUploadAlt, 
         FaCode, FaLaptopCode, 
         FaDatabase, FaLeaf, FaMountain, FaRobot, FaAtom, FaUniversity, FaCalendarAlt } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/internshippage.css';

const InternshipPage = () => {
  const [internships, setInternships] = useState([]);
  const [filteredInternships, setFilteredInternships] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTitle, setSearchTitle] = useState("");
  const [searchLocation, setSearchLocation] = useState("");
  const [selectedField, setSelectedField] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [selectedInternship, setSelectedInternship] = useState(null);
  const [isInternshipDetailsView, setIsInternshipDetailsView] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    university: '',
    graduationDate: '',
    major: '',
    resume: null,
    coverLetter: ''
  });
  const internshipsPerPage = 5;

  // Sample internship data - in a real application, this would come from your API or database
  useEffect(() => {
    const sampleInternships = [
      {
        id: 1,
        title: "Environmental Science Intern",
        location: "Seattle, WA",
        field: "environmental",
        department: "Environmental Research",
        duration: "3 months (Summer)",
        description: "Join our environmental research team to work on sustainable solutions for pressing environmental challenges.",
        responsibilities: [
          "Assist in collecting and analyzing environmental data",
          "Contribute to research projects on climate change impacts",
          "Help develop sustainable technology solutions",
          "Collaborate with senior scientists on field research",
          "Create reports and visualizations of environmental findings"
        ],
        requirements: [
          "Currently pursuing a degree in Environmental Science or related field",
          "Strong analytical and research skills",
          "Basic knowledge of data analysis tools",
          "Interest in sustainable technology and climate solutions",
          "Ability to work in field conditions when necessary"
        ]
      },
      {
        id: 2,
        title: "Geosciences Research Intern",
        location: "Denver, CO",
        field: "geoscience",
        department: "Geological Survey",
        duration: "6 months",
        description: "Work alongside our geoscience team to analyze geological data and contribute to earth science innovations.",
        responsibilities: [
          "Process and analyze geological data sets",
          "Assist in developing geological models",
          "Support field survey operations when needed",
          "Contribute to research on sustainable resource extraction",
          "Help with implementing GIS solutions for geological mapping"
        ],
        requirements: [
          "Currently pursuing a degree in Geology, Geophysics, or related field",
          "Experience with GIS software preferred",
          "Basic understanding of geological processes",
          "Strong interest in earth sciences and sustainability",
          "Good problem-solving skills and attention to detail"
        ]
      },
      {
        id: 3,
        title: "Full Stack Developer Intern",
        location: "San Francisco, CA",
        field: "fullstack",
        department: "Engineering",
        duration: "6 months",
        description: "Gain hands-on experience building web applications and services using modern technologies.",
        responsibilities: [
          "Develop and maintain web applications using React, Node.js",
          "Work with APIs and database systems",
          "Collaborate with senior developers on production code",
          "Participate in code reviews and agile development processes",
          "Assist in troubleshooting and debugging issues"
        ],
        requirements: [
          "Currently pursuing a degree in Computer Science or related field",
          "Knowledge of JavaScript, HTML, and CSS",
          "Familiarity with React, Node.js or similar technologies",
          "Basic understanding of databases and RESTful APIs",
          "Strong problem-solving skills and eagerness to learn"
        ]
      },
      {
        id: 4,
        title: "AI & ML Engineering Intern",
        location: "Boston, MA",
        field: "ai-ml",
        department: "Artificial Intelligence",
        duration: "4 months",
        description: "Join our AI research team to develop and implement machine learning solutions for real-world problems.",
        responsibilities: [
          "Assist in developing and fine-tuning machine learning models",
          "Collect and preprocess data for machine learning applications",
          "Implement and test AI algorithms",
          "Collaborate with data scientists on research projects",
          "Document research findings and technical implementations"
        ],
        requirements: [
          "Currently pursuing a degree in Computer Science, Data Science, or related field",
          "Knowledge of Python and machine learning libraries (TensorFlow, PyTorch)",
          "Understanding of basic machine learning concepts",
          "Experience with data preprocessing and analysis",
          "Strong mathematical background and analytical thinking"
        ]
      },
      {
        id: 5,
        title: "Quantum Computing Intern",
        location: "Austin, TX",
        field: "quantum",
        department: "Quantum Research",
        duration: "6 months",
        description: "Work on cutting-edge quantum computing research and applications alongside our expert team.",
        responsibilities: [
          "Assist in quantum algorithm development and testing",
          "Help implement quantum simulations and models",
          "Support research on quantum error correction",
          "Contribute to quantum software development projects",
          "Collaborate with researchers on quantum computing applications"
        ],
        requirements: [
          "Currently pursuing an advanced degree in Physics, Computer Science, or related field",
          "Strong background in linear algebra and quantum mechanics",
          "Familiarity with quantum computing concepts",
          "Experience with Python, Qiskit, or other quantum programming frameworks preferred",
          "Excellent problem-solving skills and ability to learn complex concepts quickly"
        ]
      },
      {
        id: 6,
        title: "Renewable Energy Engineering Intern",
        location: "Portland, OR",
        field: "energy",
        department: "Energy Systems",
        duration: "4 months",
        description: "Participate in developing innovative renewable energy solutions for sustainable power generation.",
        responsibilities: [
          "Support research on renewable energy systems",
          "Assist in designing and testing energy-efficient components",
          "Collect and analyze data from energy experiments",
          "Help with simulation and modeling of energy systems",
          "Contribute to technical documentation and reports"
        ],
        requirements: [
          "Currently pursuing a degree in Electrical Engineering, Energy Engineering, or related field",
          "Understanding of energy systems and renewable technologies",
          "Basic knowledge of electrical systems and design",
          "Interest in sustainability and clean energy",
          "Strong analytical and technical skills"
        ]
      }
    ];
    
    setInternships(sampleInternships);
    setFilteredInternships(sampleInternships);
    setTotalPages(Math.ceil(sampleInternships.length / internshipsPerPage));
  }, []);

  // Filter internships based on search criteria
  useEffect(() => {
    let results = internships;
    
    if (searchTitle.trim() !== "") {
      results = results.filter(internship => 
        internship.title.toLowerCase().includes(searchTitle.toLowerCase())
      );
    }
    
    if (searchLocation.trim() !== "") {
      results = results.filter(internship => 
        internship.location.toLowerCase().includes(searchLocation.toLowerCase())
      );
    }
    
    if (selectedField !== "") {
      results = results.filter(internship => internship.field === selectedField);
    }
    
    setFilteredInternships(results);
    setTotalPages(Math.ceil(results.length / internshipsPerPage));
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTitle, searchLocation, selectedField, internships]);

  // Get current internships for pagination
  const indexOfLastInternship = currentPage * internshipsPerPage;
  const indexOfFirstInternship = indexOfLastInternship - internshipsPerPage;
  const currentInternships = filteredInternships.slice(indexOfFirstInternship, indexOfLastInternship);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // View internship details
  const viewInternshipDetails = (internshipId) => {
    const internship = internships.find(i => i.id === internshipId);
    setSelectedInternship(internship);
    setIsInternshipDetailsView(true);
    window.scrollTo(0, 0);
  };

  // Go back to internship listings
  const backToInternshipListings = () => {
    setIsInternshipDetailsView(false);
    setSelectedInternship(null);
  };

  // Handle input change for application form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle file change for resume upload
  const handleFileChange = (e) => {
    setFormData({
      ...formData,
      resume: e.target.files[0]
    });
  };
  
  // Handle application form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    alert("Internship application submitted successfully!");
    setIsInternshipDetailsView(false);
    setSelectedInternship(null);
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      university: '',
      graduationDate: '',
      major: '',
      resume: null,
      coverLetter: ''
    });
  };



  // Marquee component for scrolling internship fields
  const InternshipFieldsMarquee = () => {
    const internshipFields = [
      { name: "Environmental Sciences", icon: <FaLeaf /> },
      { name: "Geosciences", icon: <FaMountain /> },
      { name: "Full Stack Development", icon: <FaCode /> },
      { name: "AI & Machine Learning", icon: <FaRobot /> },
      { name: "Quantum Computing", icon: <FaAtom /> },
      { name: "Cloud Computing", icon: <FaCloudUploadAlt /> },
      { name: "Renewable Energy", icon: <FaGlobe /> },
      { name: "Data Science", icon: <FaDatabase /> },
      { name: "Technology Research", icon: <FaLaptopCode /> },
    ];

    return (
      <div className="marquee-container">
        <div className="marquee-content">
          {internshipFields.concat(internshipFields).map((field, index) => (
            <div key={index} className="marquee-item">
              <span className="marquee-icon">{field.icon}</span>
              <span className="marquee-text">{field.name}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Hero section component
  const HeroSection = () => {
    return (
      <div className="hero-section7">
        <div className="hero-overlay">
          <Container>
            <Row className="align-items-center hero-content">
              <Col md={7}>
                <h1 className="hero-title">Launch your career with us</h1>
                <p className="hero-subtitle">
                  Gain real-world experience in cutting-edge fields like environmental sciences, 
                  geosciences, full stack development, AI/ML engineering, quantum computing, and more.
                  Our internships provide the perfect opportunity to learn, grow, and make a difference.
                </p>
                <Form className="hero-form">
                  <Form.Group className="mb-3">
                    <Form.Control type="email" placeholder="Email address..." />
                  </Form.Group>
                  <Button variant="primary" type="submit" className="sign-up-btn">
                    Get internship alerts
                  </Button>
                </Form>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    );
  };

  // Internship program highlights component
  const ProgramHighlights = () => {
    return (
      <section className="program-highlights py-5">
        <Container>
          <h2 className="text-center mb-2">Why Intern With Us?</h2> 
          <p className='text-center mb-1'> Learn from IITians  & Industry Experts </p>
          <Row>
            <Col md={4} className="mb-4">
              <Card className="highlight-card h-100">
                <Card.Body className="text-center">
                  <div className="icon-container mb-3">
                    <FaLaptopCode size={40} />
                  </div>
                  <Card.Title>Hands-on Experience</Card.Title>
                  <Card.Text>
                    Work on real projects alongside industry experts and gain practical skills that will set you apart in your field.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="highlight-card h-100">
                <Card.Body className="text-center">
                  <div className="icon-container mb-3">
                    <FaUniversity size={40} />
                  </div>
                  <Card.Title>Mentorship Program</Card.Title>
                  <Card.Text>
                    Each intern is paired with a dedicated mentor who provides guidance, feedback, and career advice throughout the internship.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="highlight-card h-100">
                <Card.Body className="text-center">
                  <div className="icon-container mb-3">
                    <FaBriefcase size={40} />
                  </div>
                  <Card.Title>Career Pathways</Card.Title>
                  <Card.Text>
                    Many of our full-time employees started as interns. Showcase your talent and potentially secure a job offer upon graduation.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    );
  };

  // Internship listings section component
  const InternshipListings = () => {
    return (
      <section className="internship-listings-section py-5">
        <Container>
          <h2 className="text-center mb-5">Available Internship Opportunities</h2>
          <p className="text-center mb-4">
            Explore our current internship openings and find the perfect opportunity to kickstart your career
          </p>
          
          <Row className="mb-4">
            <Col md={5}>
              <Form.Group>
                <div className="search-input">
                  <FaBriefcase className="search-icon" />
                  <Form.Control 
                    type="text" 
                    placeholder="Search by keyword" 
                    value={searchTitle}
                    onChange={(e) => setSearchTitle(e.target.value)}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group>
                <div className="search-input">
                  <FaMapMarkerAlt className="search-icon" />
                  <Form.Control 
                    type="text" 
                    placeholder="Search by location" 
                    value={searchLocation}
                    onChange={(e) => setSearchLocation(e.target.value)}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Button variant="primary" className="w-100 search-btn">
                Search
              </Button>
            </Col>
          </Row>
          
          <Row className="mb-4">
            <Col>
              <div className="field-filters">
                <Button 
                  variant={selectedField === "" ? "primary" : "outline-primary"}
                  className="me-2 mb-2"
                  onClick={() => setSelectedField("")}
                >
                  All Fields
                </Button>
                <Button 
                  variant={selectedField === "environmental" ? "primary" : "outline-primary"}
                  className="me-2 mb-2"
                  onClick={() => setSelectedField("environmental")}
                >
                  <FaLeaf className="me-1" /> Environmental Sciences
                </Button>
                <Button 
                  variant={selectedField === "geoscience" ? "primary" : "outline-primary"}
                  className="me-2 mb-2"
                  onClick={() => setSelectedField("geoscience")}
                >
                  <FaMountain className="me-1" /> Geosciences
                </Button>
                <Button 
                  variant={selectedField === "fullstack" ? "primary" : "outline-primary"}
                  className="me-2 mb-2"
                  onClick={() => setSelectedField("fullstack")}
                >
                  <FaCode className="me-1" /> Full Stack Development
                </Button>
                <Button 
                  variant={selectedField === "ai-ml" ? "primary" : "outline-primary"}
                  className="me-2 mb-2"
                  onClick={() => setSelectedField("ai-ml")}
                >
                  <FaRobot className="me-1" /> AI & ML
                </Button>
                <Button 
                  variant={selectedField === "quantum" ? "primary" : "outline-primary"}
                  className="me-2 mb-2"
                  onClick={() => setSelectedField("quantum")}
                >
                  <FaAtom className="me-1" /> Quantum Computing
                </Button>
                <Button 
                  variant={selectedField === "energy" ? "primary" : "outline-primary"}
                  className="me-2 mb-2"
                  onClick={() => setSelectedField("energy")}
                >
                  <FaGlobe className="me-1" /> Renewable Energy
                </Button>
              </div>
            </Col>
          </Row>
          
          {filteredInternships.length === 0 ? (
            <div className="text-center py-5">
              <h4>No internships match your search criteria</h4>
              <p>Try adjusting your search terms or filters</p>
            </div>
          ) : (
            <>
              <Row>
                {currentInternships.map(internship => (
                  <Col md={12} key={internship.id} className="mb-4">
                    <Card className="internship-card">
                      <Card.Body>
                        <div className="d-flex justify-content-between">
                          <div>
                            <Card.Title>{internship.title}</Card.Title>
                            <Card.Subtitle className="mb-2 text-muted">
                              <FaMapMarkerAlt className="me-1" /> {internship.location}
                            </Card.Subtitle>
                            <div className="mb-3">
                              <Badge bg="primary" className="me-2">{internship.department}</Badge>
                              <Badge bg="secondary" className="me-2">
                                <FaCalendarAlt className="me-1" /> {internship.duration}
                              </Badge>
                            </div>
                            <Card.Text className="internship-description">{internship.description}</Card.Text>
                          </div>
                          <div className="d-flex align-items-center">
                            <Button variant="outline-primary" onClick={() => viewInternshipDetails(internship.id)}>
                              View Details
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
              
              <div className="d-flex justify-content-center mt-4">
                <Pagination>
                  <Pagination.Prev 
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  />
                  
                  {[...Array(totalPages).keys()].map(number => (
                    <Pagination.Item 
                      key={number + 1}
                      active={number + 1 === currentPage}
                      onClick={() => paginate(number + 1)}
                    >
                      {number + 1}
                    </Pagination.Item>
                  ))}
                  
                  <Pagination.Next 
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            </>
          )}
        </Container>
      </section>
    );
  };


  // Internship details component
  const InternshipDetails = () => {
    if (!selectedInternship) return null;
    
    return (
      <div className="internship-details-page">
        <Container className="py-5">
          <Button onClick={backToInternshipListings} variant="outline-primary" className="mb-4">
            &larr; Back to Internships
          </Button>
          
          <Card className="mb-5">
            <Card.Body>
              <h1>{selectedInternship.title}</h1>
              <div className="mb-4">
                <Badge bg="primary" className="me-2">{selectedInternship.department}</Badge>
                <Badge bg="secondary" className="me-2">
                  <FaCalendarAlt className="me-1" /> {selectedInternship.duration}
                </Badge>
                <span className="text-muted">
                  <FaMapMarkerAlt className="me-1" /> {selectedInternship.location}
                </span>
              </div>
              
              <div className="internship-description mb-4">
                <h5>Program Description</h5>
                <p>{selectedInternship.description}</p>
              </div>
              
              <div className="internship-responsibilities mb-4">
                <h5>What You'll Do</h5>
                <ul>
                  {selectedInternship.responsibilities.map((resp, index) => (
                    <li key={index}>{resp}</li>
                  ))}
                </ul>
              </div>
              
              <div className="internship-requirements mb-4">
                <h5>What We're Looking For</h5>
                <ul>
                  {selectedInternship.requirements.map((req, index) => (
                    <li key={index}>{req}</li>
                  ))}
                </ul>
              </div>
              
              <div className="text-center mt-5">
                <h3>Apply for this internship</h3>
                <p>Submit your application below to join our team for this exciting opportunity</p>
              </div>
              
              <Form onSubmit={handleSubmit} className="application-form mt-4">
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Full Name</Form.Label>
                      <Form.Control 
                        type="text" 
                        name="fullName" 
                        value={formData.fullName} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Email</Form.Label>
                      <Form.Control 
                        type="email" 
                        name="email" 
                        value={formData.email} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Phone Number</Form.Label>
                      <Form.Control 
                        type="tel" 
                        name="phone" 
                        value={formData.phone} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>University/College</Form.Label>
                      <Form.Control 
                        type="text" 
                        name="university" 
                        value={formData.university} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Major/Field of Study</Form.Label>
                      <Form.Control 
                        type="text" 
                        name="major" 
                        value={formData.major} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Expected Graduation Date</Form.Label>
                      <Form.Control 
                        type="month" 
                        name="graduationDate" 
                        value={formData.graduationDate} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Form.Group className="mb-3">
                  <Form.Label>Resume</Form.Label>
                  <Form.Control 
                    type="file" 
                    name="resume" 
                    onChange={handleFileChange} 
                    required 
                  />
                  <Form.Text className="text-muted">
                    Upload your resume (PDF, DOC, DOCX)
                  </Form.Text>
                </Form.Group>
                
                <Form.Group className="mb-4">
                  <Form.Label>Cover Letter</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={5} 
                    name="coverLetter" 
                    value={formData.coverLetter} 
                    onChange={handleInputChange} 
                    placeholder="Tell us why you're interested in this internship and how your skills align with the requirements"
                  />
                </Form.Group>
                
                <div className="text-center">
                  <Button variant="primary" type="submit" size="lg">
                    Submit Application
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  };

  // FAQ Section
  const FAQSection = () => {
    return (
      <section className="faq-section py-5">
        <Container>
          <h2 className="text-center mb-5">Frequently Asked Questions</h2>
          <Row>
            <Col md={6} className="mb-4">
              <Card className="faq-card">
                <Card.Body>
                  <h5>What are the application deadlines?</h5>
                  <p>
                    Application deadlines vary by program. Summer internships typically have 
                    application deadlines in January-February. Fall internships in May-June, 
                    and Spring internships in September-October.
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-4">
              <Card className="faq-card">
                <Card.Body>
                  <h5>Are internships paid?</h5>
                  <p>
                    Yes, all our internships offer competitive compensation packages, which vary 
                    based on the position, location, and your qualifications.
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-4">
              <Card className="faq-card">
                <Card.Body>
                  <h5>Do you offer remote internships?</h5>
                  <p>
                    Some positions offer remote or hybrid arrangements, while others require 
                    on-site presence due to the nature of the work. Each listing will specify 
                    the work arrangements.
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-4">
              <Card className="faq-card">
                <Card.Body>
                  <h5>What is the selection process?</h5>
                  <p>
                    Our selection process typically includes resume screening, 1-2 rounds of 
                    interviews, and potentially a technical assessment or case study depending 
                    on the role.
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-4">
              <Card className="faq-card">
                <Card.Body>
                  <h5>Do you sponsor international students?</h5>
                  <p>
                    Yes, we welcome international students and provide necessary documentation 
                    for CPT/OPT as applicable. You must be authorized to work in the country 
                    where the internship is located.
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-4">
              <Card className="faq-card">
                <Card.Body>
                  <h5>Can internships lead to full-time positions?</h5>
                  <p>
                    Absolutely! Many of our full-time employees started as interns. High-performing 
                    interns are often considered for full-time positions upon graduation.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <div className="text-center mt-4">
            <p>Have more questions? Contact our internship coordinator:</p>
            <Button variant="outline-primary me-3"> Call us :- +91 8639449423</Button>
            <Button variant="outline-dark me-3"> Whats App us :- +91 8333880649</Button>
          </div>
        </Container>
      </section>
    );
  };

  // Call to Action section
  const CTASection = () => {
    return (
      <section className="cta-section py-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} className="text-center">
              <h2>Ready to kickstart your career?</h2>
              <p className="mb-4">
                Don't see an internship that matches your interests? Submit your resume to our talent pool 
                and we'll contact you when a suitable position becomes available.
              </p>
              <Button variant="primary" size="lg">Join our talent pool</Button>
            </Col>
          </Row>
        </Container>
      </section>
    );
  };

  
  return (
    <div className="internship-page">
     
      {!isInternshipDetailsView ? (
        <>
          <HeroSection />
          <InternshipFieldsMarquee />
          <ProgramHighlights />
          <InternshipListings />
         
          <FAQSection />
          <CTASection />
          
        </>
      ) : (
        <>
          <InternshipDetails />
        
        </>
      )}
    </div>
  );
};

export default InternshipPage;